import type { AuthSession, AuthUser } from './types';

/**
 * Session storage. Ported from the proven `web/js/api-client.js` contract,
 * including one non-obvious rule learned there:
 *
 *   The tenant SLUG is stored separately from the tenant ID and deliberately
 *   SURVIVES sign-out — the public white-label branding endpoint is keyed by
 *   slug, so clearing it would leave the login screen unbranded for a
 *   returning user.
 */

const KEYS = {
  access: 'crm_access',
  refresh: 'crm_refresh',
  tenant: 'crm_tenant',
  user: 'crm_user',
} as const;

/** Outside KEYS so `clear()` cannot wipe it. */
const SLUG_KEY = 'crm_tenant_slug';

const UUID_RE = /^[0-9a-f-]{36}$/i;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // storage disabled (private mode / embedded webview)
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* non-fatal: the session just won't persist across reloads */
  }
}

export const session = {
  get accessToken(): string | null {
    return read(KEYS.access);
  },
  get refreshToken(): string | null {
    return read(KEYS.refresh);
  },
  get tenant(): string | null {
    return read(KEYS.tenant);
  },
  /** Last human-typed tenant slug — survives sign-out, for public branding. */
  get tenantSlug(): string | null {
    return read(SLUG_KEY);
  },
  get user(): AuthUser | null {
    const raw = read(KEYS.user);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },
  get isAuthenticated(): boolean {
    return !!read(KEYS.access);
  },

  setTenant(value: string): void {
    if (!value) return;
    write(KEYS.tenant, value);
    // A uuid is an id, not a slug — only remember human-typed slugs.
    if (!UUID_RE.test(value)) write(SLUG_KEY, value);
  },

  save(data: Partial<AuthSession>): void {
    if (data.accessToken) write(KEYS.access, data.accessToken);
    if (data.refreshToken) write(KEYS.refresh, data.refreshToken);
    if (data.user) {
      write(KEYS.user, JSON.stringify(data.user));
      if (data.user.tenantId) write(KEYS.tenant, data.user.tenantId);
    }
  },

  clear(): void {
    Object.values(KEYS).forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    });
    // SLUG_KEY intentionally retained — see the note above.
  },
};
